/***
 * Code was initially generated by Gemini 2.5 Pro, low on time so it's all in the same file (vom), no time rn to refactor so will add as tech debt to backlog
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import mapboxgl, { LngLatLike, Map, Marker, Popup } from "mapbox-gl";
import { YStack, Input, XStack } from "tamagui";
import { baseApiUrl, mapboxStyle, mapboxToken, writeToLog } from "../../const";
import { Button, primary2 } from "@shane-collective/design-system";
import { Title } from "../../components/webLocationDialog/webLocationDialog.styles";
import {
  Body,
  Helptext,
} from "../../components/webWelcomeModal/webWelcomeModal.styles";
import { Label } from "../../components/webSettingsDialog/webSettingsDialog.styles";
import "./style.css";
import { useShaneClientContext } from "../../core/shaneClient/ShaneClientProvider";
import {
  faCheckCode,
  faSendCode,
  fetchBark,
  fetchBarkCommunicationUuid,
} from "../../client";
import { useAppState } from "../../core/appState/UseAppStateZustand";
import { getToken } from "../../core/api/getToken";
import { decodeUrlSafeBase64ToUuid } from "../../functions/Functions";
// import { generateDevToken } from "./jwtDevToken";

mapboxgl.accessToken = mapboxToken;

type VerificationStatus =
  | "idle"
  | "requestingOtp"
  | "awaitingOtp"
  | "verifyingOtp"
  | "verified"
  | "loadingHistory"
  | "error";
type LocationHistoryItem = {
  coord: [number, number];
  timestamp: Date;
};

// --- Component ---
const LiveLocationViewer: React.FC = () => {
  const { shaneClient } = useShaneClientContext();

  // --- Router ---
  const barkCommunicationUuid  = decodeUrlSafeBase64ToUuid(useParams<{ token: string }>().token ?? ''); // Get session token from URL

  // --- State ---
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus>(barkCommunicationUuid ? "idle" : "error");
  const [errorMessage, setErrorMessage] = useState<string | null>(
    barkCommunicationUuid ? null : "Invalid or missing access link."
  );
  // const [phoneNumber, setPhoneNumber] = useState<string>(''); // Store if needed for display
  const [otp, setOtp] = useState<string>("");
  const [wsAuthToken, setWsAuthToken] = useState<string | null>(null); // The JWT for WS/History
  const [locationHistory, setLocationHistory] = useState<LocationHistoryItem[]>(
    []
  );
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string>(
    "To get access to live location, click the button below to request a one time password"
  );
  const [barkCommunicationId, setBarkCommunicationId] = useState<number>();
  const [phoneNumber, setPhoneNumber] = useState<string>();
  // --- Refs ---
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const markerRef = useRef<Marker | null>(null);
  const popupRef = useRef<Popup | null>(null);
  const webSocketRef = useRef<WebSocket | null>(null);

  // --- Constants ---
  //@ts-ignore
  const WEBSOCKET_URL_BASE = process.env.REACT_APP_WSS_URL ?? "";

  useEffect(() => {
    const { setAccessToken, setAccessTokenExp } = useAppState.getState();

    const refreshToken = async () => {
      const data = await getToken();

      if (data) {
        setAccessToken(data.token);
        setAccessTokenExp(data.exp);

        const now = new Date();
        const timeUntilExpiration =
          new Date(data.exp).getTime() - now.getTime();

        if (timeUntilExpiration <= 0) {
          data.token = await refreshToken();
          writeToLog("Token refreshed immediately!");
        } else {
          writeToLog(
            `Token expires in ${
              timeUntilExpiration / 1000
            } seconds. Waiting for expiration...`
          );

          setTimeout(async () => {
            await refreshToken();
          }, timeUntilExpiration);
        }
      }
    };

    refreshToken();

    if (!barkCommunicationUuid) {
      setErrorMessage("Invalid link");
      setVerificationStatus("error");
      return;
    }

    const getBarkCommunicationAndVerify = async () => {
      console.log('barkCommunicationUuid',barkCommunicationUuid)
      // get token
      const { data, error } = await fetchBarkCommunicationUuid({
        client: shaneClient,
        path: {
          uuid: barkCommunicationUuid,
        },
      });

      console.log("communication data", data);
      setPhoneNumber(data?.phoneNumber);

      if (error || !data) {
        // setErrorMessage("Invalid link");
        // setVerificationStatus("error");
        return;
      }

      if (data) {
        // get bark
        const { data: barkData, error: barkError } = await fetchBark({
          client: shaneClient,
          path: {
            barkId: (data?.barkId ?? -1).toString(),
          },
        });

        console.log("bark", barkData, barkError);
        if (barkError || !barkData) {
          setErrorMessage("Invalid link");
          setVerificationStatus("error");
          return;
        }

        const barkDataObj = JSON.parse(barkData as string)


        if (barkData) {
          if (new Date() >= new Date(barkDataObj.endDateTime ?? "")) {
            //window closed
            setErrorMessage("Bark window closed, user has marked themselves as safe");
            setVerificationStatus("error");
            return;
          }

          setBarkCommunicationId(data.barkCommunicationId);
        }
      }
    };

    setTimeout(() => {
      getBarkCommunicationAndVerify();
    }, 1000);
  }, [barkCommunicationUuid, shaneClient]);

  // --- API Call Logic ---
  const handleRequestOtp = useCallback(async () => {
    //if no token, set error
    if (!barkCommunicationUuid) return;

    setVerificationStatus("requestingOtp");
    setErrorMessage(null);
    setStatusMessage("Requesting OTP...");

    try {
      const { data, error } = await faSendCode({
        client: shaneClient,
        query: {
          phoneNumber,
        },
      });

      if (error) {
        throw new Error(error as any);
      } else {
        setVerificationStatus("awaitingOtp");
        setStatusMessage("OTP Sent. Please check your phone");
      }

      console.log(data);
    } catch (error: any) {
      setErrorMessage(error.message);
      setVerificationStatus("error");
      setStatusMessage("Failed to send OTP");
    }
  }, [barkCommunicationUuid, phoneNumber, shaneClient]);


  const handleVerifyOtp = useCallback(async () => {
    if (!barkCommunicationUuid || !otp) return;

    setVerificationStatus("verifyingOtp");
    setErrorMessage(null);
    setStatusMessage("Verifying OTP...");

    try {
      const { data, error } = await faCheckCode({
        client: shaneClient,
        query: {
          code: otp,
          phoneNumber,
          barkCommunicationId: (barkCommunicationId ?? 0).toString(),
        },
      } as any);

      if (error) {
        throw new Error(error as any);
      }

      if (!data?.token) {
        throw new Error(
          "Verification succeeded but did not receive auth token."
        );
      }
      setWsAuthToken(data.token); // Store the JWT
      setVerificationStatus("loadingHistory"); // Move to next step
      setStatusMessage("Verification successful. Loading history...");
      //TODO mark the communication as connected
    } catch (error: any) {
      setErrorMessage(error.message);
      setVerificationStatus("error");
      setStatusMessage("Verification failed.");
    }
  }, [barkCommunicationUuid, otp, phoneNumber, barkCommunicationId, shaneClient]);

  const fetchHistory = useCallback(async () => {
    if (!barkCommunicationUuid || !wsAuthToken) return;
    try {
      const response = await fetch(
        `${baseApiUrl}/communication/${barkCommunicationId}/location?pageSize=1000`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${wsAuthToken}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to load history");
      }

      const data = await response.json();

      if (!data) {
        setVerificationStatus("verified"); // Ready to show map
        setStatusMessage("No history to load.");
        return;
      }

      const formattedHistory = (data as any[])?.map((item) => ({
        coord: [item.lng, item.lat],
        timestamp: new Date(item?.timestamp ?? ""), // Assumes backend sends ISO string or ms
      }));
      setLocationHistory(formattedHistory as LocationHistoryItem[]);
      setVerificationStatus("verified"); // Ready to show map
      setStatusMessage("Location history loaded.");


      if (!formattedHistory) {
        return;
      }

      // flyto first location
      if (formattedHistory.length > 0 && mapRef?.current) {
        mapRef.current.flyTo({
          center: formattedHistory[1].coord as LngLatLike,
          zoom: 6,
          speed: 1.5,
        });
      }
    } catch (error: any) {
      console.error("Get History Error:", error);
      setErrorMessage(
        `Error loading history: ${error.message}. Showing live data only.`
      );
      setLocationHistory([]); // Clear history on error
      setVerificationStatus("verified"); // Still proceed to show map, but without history
      setStatusMessage("Error loading history.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsAuthToken, barkCommunicationId, barkCommunicationUuid]);

  // --- Effects ---

  // Effect to fetch history once wsAuthToken is available
  useEffect(() => {
    if (verificationStatus === "loadingHistory" && wsAuthToken) {
      fetchHistory();
    }
  }, [verificationStatus, wsAuthToken, fetchHistory]);

  // Effect for Map Initialization
  useEffect(() => {
    // Only initialize map if verified, history loaded (or failed), and container is ready
    if (
      verificationStatus !== "verified" ||
      !mapContainerRef.current ||
      mapRef.current
    ) {
      return; // Exit if not ready, already initialized, or container not mounted
    }

    setStatusMessage("Initializing map...");
    const initialCoords =
      locationHistory.length > 0
        ? locationHistory[locationHistory.length - 1].coord
        : [-2.0889, 53.4106]; // Default or last known
    const initialZoom = locationHistory.length > 0 ? 15 : 12;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapboxStyle,
      center: initialCoords as LngLatLike,
      zoom: initialZoom,
    });
    mapRef.current = map; // Store map instance

    map.on("load", () => {
      console.log("Map loaded event");
      setIsMapLoaded(true); // Mark map as ready for layers/sources

      // Add sources (initially potentially empty or with history)
      map.addSource("route-source", {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: locationHistory.map((h) => h.coord),
          },
        },
      });
      map.addSource("historical-points-source", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      // Add layers
      map.addLayer({
        id: "route-layer",
        type: "line",
        source: "route-source",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "#0A6666",
          "line-width": 4,
          "line-opacity": 0.8,
        },
      });
      map.addLayer({
        id: "historical-points-layer",
        type: "circle",
        source: "historical-points-source",
        paint: {
          "circle-radius": 5,
          "circle-color": "#FF8C00",
          "circle-stroke-width": 1,
          "circle-stroke-color": "#ffffff",
        },
      });

      // Add Controls
      map.addControl(new mapboxgl.NavigationControl(), "top-right");
      // Add your LogoControl if ported to React/TS or use simple elements

      // Initialize Popup
      popupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      });

      // Add Popup Listeners
      map.on("mouseenter", "historical-points-layer", handlePointMouseEnter);
      map.on("mouseleave", "historical-points-layer", handlePointMouseLeave);
      map.on("click", "historical-points-layer", handlePointClick); // Re-use mouseenter logic for click

      // Update marker and points sources with initial history
      setTimeout(() => {
        updateMapElements(locationHistory);
      }, 1000);

      // Connect WebSocket now that map is ready
      connectWebSocket();
    });

    // Cleanup function
    return () => {
      console.log("Cleaning up map");
      // Remove listeners first
      if (map) {
        map.off("mouseenter", "historical-points-layer", handlePointMouseEnter);
        map.off("mouseleave", "historical-points-layer", handlePointMouseLeave);
        map.off("click", "historical-points-layer", handlePointClick);
        map.remove(); // Clean up Mapbox instance
      }
      mapRef.current = null;
      setIsMapLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationStatus]); // Run effect when verificationStatus changes to 'verified'

  // --- Map Interaction Callbacks ---
  const handlePointMouseEnter = useCallback((e: mapboxgl.MapMouseEvent) => {
    if (
      !mapRef.current ||
      !popupRef.current ||
      !e.features ||
      e.features.length === 0
    )
      return;
    mapRef.current.getCanvas().style.cursor = "pointer";
    const feature = e.features[0];
    if (feature.geometry.type === "Point") {
      const coordinates = feature.geometry.coordinates.slice() as [
        number,
        number
      ];
      const timestampStr = feature.properties?.timestamp;
      const formattedTimestamp = timestampStr
        ? new Date(timestampStr).toLocaleString()
        : "No timestamp";
      popupRef.current
        .setLngLat(coordinates)
        .setHTML(`${formattedTimestamp}`)
        .addTo(mapRef.current);
    }
  }, []);

  const handlePointMouseLeave = useCallback(() => {
    // if (!mapRef.current || !popupRef.current) return;
    // mapRef.current.getCanvas().style.cursor = "";
    // popupRef.current.remove();
  }, []);

  // Re-use mouseenter logic for click
  const handlePointClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      handlePointMouseEnter(e);
    },
    [handlePointMouseEnter]
  );

  // --- Helper to Update Map Elements ---
  const updateMapElements = (history: LocationHistoryItem[]) => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) {
      console.log("Map not ready for source update yet.");
      return; // Guard against updating before style is loaded
    }

    // Update Line Source
    const routeSource = map.getSource("route-source") as mapboxgl.GeoJSONSource;


    if (routeSource) {
      const lineCoords = history.map((item) => item.coord);
      routeSource.setData({
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: lineCoords },
      });
    }

    // Update Points Source (all except latest)
    const pointsSource = map.getSource(
      "historical-points-source"
    ) as mapboxgl.GeoJSONSource;
    if (pointsSource) {
      const historicalFeatures = history.slice(0, -1).map((item) => ({
        type: "Feature" as const, // Assert type for TS
        properties: { timestamp: item.timestamp.toISOString() },
        geometry: { type: "Point" as const, coordinates: item.coord },
      }));
      pointsSource.setData({
        type: "FeatureCollection" as const,
        features: historicalFeatures,
      });
    }

    // Update Marker Position (to the latest point)
    if (history.length > 0) {
      const latestCoord = history[history.length - 1].coord;


      if (!markerRef.current) {
        const el = document.createElement("div");
        el.className = "custom-marker";
        markerRef.current = new mapboxgl.Marker({
          element: el,
          anchor: "bottom",
        })
          .setLngLat(latestCoord)
          .addTo(map);
      } else {
        markerRef.current.setLngLat(latestCoord);
      }
      if (locationHistory.length === 1 && mapRef.current) {
        mapRef.current.flyTo({ center: latestCoord, zoom: 15, speed: 1.5 });
      }
    }

    console.log(
      `Map elements updated. History length: ${history.length}`,
      locationHistory.length
    );
  };

  // Effect to update map sources when locationHistory changes
  useEffect(() => {
    if (isMapLoaded && mapRef.current) {
      updateMapElements(locationHistory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationHistory, isMapLoaded]);

  // --- WebSocket Connection Logic ---
  const connectWebSocket = useCallback(() => {
    if (!wsAuthToken || webSocketRef.current) {
      console.log(
        "WebSocket connection skipped (no token or already connected)."
      );
      return;
    }

    const wsUrl =
      WEBSOCKET_URL_BASE +
      "?wsAuthToken=" +
      wsAuthToken +
      "&barkCommunicationId=" +
      barkCommunicationId;
    console.log("Connecting WebSocket...", wsUrl);
    setStatusMessage("Connecting to live updates...");

    const ws = new WebSocket(wsUrl);
    webSocketRef.current = ws;

    ws.onopen = () => {
      console.log("WebSocket connected.");
      setStatusMessage("Connected. Waiting for live location...");
    };

    ws.onmessage = (event) => {
      try {
        console.log("WS Message received:", event.data);
        const locationData = JSON.parse(event.data);
        if (
          locationData &&
          typeof locationData.lat === "number" &&
          typeof locationData.lng === "number"
        ) {
          const coordinates: [number, number] = [
            locationData.lng,
            locationData.lat,
          ];
          const timestamp = new Date(locationData.timestamp);
          const newItem: LocationHistoryItem = {
            coord: coordinates,
            timestamp,
          };

          // Update state
          setLocationHistory((prevHistory) => [...prevHistory, newItem]);

          setStatusMessage(`Live Location: ${timestamp.toLocaleTimeString()}`);
        }
      } catch (e) {
        console.error("Failed to process WebSocket message:", e);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket Error:", error);
      setStatusMessage("Live connection error.");
    };

    ws.onclose = (event) => {
      console.log("WebSocket closed:", event);
      if (webSocketRef.current === ws) {
        // Avoid cleanup if a new socket was created quickly
        webSocketRef.current = null;
        setStatusMessage("Live connection closed.");
        // Consider reconnection logic? Would need re-verification probably.
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsAuthToken]);

  // Effect to cleanup WebSocket on component unmount
  useEffect(() => {
    return () => {
      console.log("Cleaning up WebSocket connection.");
      webSocketRef.current?.close();
      webSocketRef.current = null;
    };
  }, []);

  // --- Render Logic ---
  const renderVerification = () => (
    // Use Tamagui components here (YStack, Input, Button, Text, Spinner)
    <YStack id="verification-ui" alignItems="center" justifyContent="center" padding={20}>
       {!["Invalid link","Bark window closed, user has marked themselves as safe"].includes(errorMessage ?? '') &&<Title>Verify Access</Title>}
      {errorMessage && (
        <Helptext style={{ color: "red" }}>{errorMessage}</Helptext>
      )}
      {!["Invalid link","Bark window closed, user has marked themselves as safe"].includes(errorMessage ?? '') && <>
      <Body color={primary2}>{statusMessage}</Body>

      {verificationStatus === "idle" ||
      verificationStatus === "requestingOtp" ||
      verificationStatus === "error" ? (
        <Button
          width="60%"
          type="primary"
          id="request-otp-btn"
          onClick={handleRequestOtp}
          disabled={
            verificationStatus === "requestingOtp" ||
            !barkCommunicationUuid ||
            !phoneNumber
          }
          label={
            verificationStatus === "requestingOtp"
              ? "Sending..."
              : "Send Verification Code via SMS"
          }
        />
      ) : null}

      {verificationStatus === "awaitingOtp" ||
      verificationStatus === "verifyingOtp" ? (
        <YStack id="otp-section" gap={10}>
          <XStack>
            <Label htmlFor="otp-code">Enter code:</Label>
            <Input
              placeholder="One time password"
              width="100%"
              minHeight={"35px"}
              id="otp-code"
              value={otp}
              onChange={(e: any) => setOtp(e.target.value)}
              fontSize="$md"
              disabled={verificationStatus === "verifyingOtp"}
            ></Input>
          </XStack>
          <Button
            type="primary"
            id="verify-otp-btn"
            onClick={handleVerifyOtp}
            disabled={verificationStatus === "verifyingOtp" || otp.length !== 6}
            label={
              verificationStatus === "verifyingOtp"
                ? "Verifying..."
                : "Verify Code"
            }
          />
        </YStack>
      ) : null}</>}
    </YStack>
  );

  const renderMap = () => (
    <>
      {/* Status overlay */}
      <div
        id="status"
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          background: "rgba(255,255,255,0.8)",
          padding: "5px 10px",
          zIndex: 1,
          borderRadius: "3px",
          fontSize: "12px",
        }}
      >
        {statusMessage}
      </div>
      {/* Map container */}
      <div
        id="map"
        ref={mapContainerRef}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          width: "100%",
        }}
      />
    </>
  );

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100vh",
      }}
    >
      {verificationStatus === "verified" ||
      verificationStatus === "loadingHistory"
        ? renderMap()
        : renderVerification()}
    </div>
  );
};

export default LiveLocationViewer;
